import React from "react";
import styled from "styled-components";
import { colors } from "../../../../styles/style";
import { SmallestText } from "../../../shared/SharedStyles";
import { FirstWord } from "../../../../interface/FirstWordInterface";
import {
  calculateAgeInMonthsAt,
  formatAgeInMonths,
  formatTimestampToDayMonth,
  formatTimestampToMonthYear,
} from "../../../../common/helpers";
import { SparkleIcon } from "./FirstWordsShared";

interface FirstWordsGardenProps {
  words: FirstWord[];
  earliestWordId?: string;
  dateOfBirth?: number;
  onWordClick: (word: FirstWord) => void;
}

interface WordGroup {
  label: string;
  words: FirstWord[];
}

const getGroupLabel = (word: FirstWord, dateOfBirth?: number) =>
  dateOfBirth === undefined
    ? formatTimestampToMonthYear(word.said_on)
    : formatAgeInMonths(
        calculateAgeInMonthsAt(dateOfBirth, word.said_on * 1000)
      );

// Expects words newest first, so every label forms one contiguous run.
const groupWords = (words: FirstWord[], dateOfBirth?: number): WordGroup[] =>
  words.reduce<WordGroup[]>((groups, word) => {
    const label = getGroupLabel(word, dateOfBirth);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.label === label) {
      lastGroup.words.push(word);
    } else {
      groups.push({ label, words: [word] });
    }
    return groups;
  }, []);

export const FirstWordsGarden: React.FC<FirstWordsGardenProps> = ({
  words,
  earliestWordId,
  dateOfBirth,
  onWordClick,
}) => (
  <Garden>
    {groupWords(words, dateOfBirth).map((group) => (
      <Group key={group.label}>
        <GroupHeading>
          <GroupLabel>{group.label}</GroupLabel>
          <GroupDivider />
        </GroupHeading>
        <Chips>
          {group.words.map((word) => {
            const isEarliest = word.id === earliestWordId;
            return (
              <Chip
                key={word.id}
                type="button"
                $isEarliest={isEarliest}
                onClick={() => onWordClick(word)}
              >
                <ChipWordRow>
                  <ChipWord>{word.word}</ChipWord>
                  {isEarliest && <SparkleIcon size={10} />}
                </ChipWordRow>
                <ChipDate>{formatTimestampToDayMonth(word.said_on)}</ChipDate>
              </Chip>
            );
          })}
        </Chips>
      </Group>
    ))}
  </Garden>
);

const Garden = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 20px 24px 0;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GroupHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const GroupLabel = styled.span`
  ${SmallestText}
  color: ${colors.softBrown};
  white-space: nowrap;
`;

const GroupDivider = styled.div`
  flex-grow: 1;
  height: 1px;
  background-color: ${colors.borderWhite};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

interface ChipProps {
  $isEarliest: boolean;
}

const Chip = styled.button<ChipProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  max-width: 100%;
  min-height: 52px;
  padding: 8px 16px;
  background-color: ${(props) =>
    props.$isEarliest ? colors.softPink : colors.white};
  border: 1px solid
    ${(props) => (props.$isEarliest ? colors.softPink : colors.borderWhite)};
  border-radius: 20px 20px 20px 6px;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
`;

const ChipWordRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ChipWord = styled.span`
  color: ${colors.darkBrown};
  font-size: 16px;
  font-weight: 450;
  letter-spacing: -0.24px;
  overflow-wrap: anywhere;
`;

const ChipDate = styled.span`
  color: ${colors.softBrown};
  font-size: 12px;
  font-weight: 450;
`;
